import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useStaffPush } from "../hooks/useStaffPush";
import { useAuth } from "../auth/AuthContext";
import HelpBadge from "./HelpBadge";

/**
 * The staff push opt-in, on the Health alerting page.
 *
 * WHY IT LIVES HERE AND NOT IN A GLOBAL MENU. The prompt has to come from an
 * explicit gesture (a denied notification permission is sticky per origin and
 * effectively un-askable afterwards), and it has to come with the context that
 * makes the answer meaningful: what gets pushed, and to whom. Health alerting
 * is where an operator is already deciding both.
 *
 * IT NAMES THE AUDIENCE RULE OUT LOUD. Subscribing is necessary but not
 * sufficient - a digest is pushed to the addresses on the rule's recipient list
 * (or the segment address book it inherits), matched to subscriptions by email.
 * Leaving that implicit is how somebody ends up trusting a channel that was
 * never going to reach them.
 */
export default function StaffPushOptIn() {
  const { user } = useAuth();
  const { state, busy, error, otherDevices, enable, disable } = useStaffPush();

  const email = user?.user ?? "your account";

  return (
    <Card variant="outlined" sx={{ p: 2.5, position: "relative" }}>
      <HelpBadge title="Push notifications are per browser: enabling this here does not enable it on your phone, and turning it off on one machine leaves the others alone. Alerts are pushed to the same recipient lists that receive the email digest, matched by your account's email address." />
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: 600 }}>
            Push notifications on this browser
          </Typography>
          {state === "on" && (
            <Chip size="small" color="success" label="Enabled" />
          )}
          {state === "off" && (
            <Chip size="small" variant="outlined" label="Not enabled" />
          )}
          {state === "denied" && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Blocked"
            />
          )}
        </Stack>

        {state === "loading" && (
          <Typography variant="body2" color="text.secondary">
            Checking…
          </Typography>
        )}

        {state === "unsupported" && (
          <Typography variant="body2" color="text.secondary">
            This browser has no Push API, so alerts can only reach you by email
            here. Chrome, Edge and Firefox support it on desktop; on iOS the CMS
            must be added to the Home Screen first.
          </Typography>
        )}

        {state === "unconfigured" && (
          <Alert severity="info" variant="outlined">
            This deployment has no Web Push identity configured (
            <code>VAPID_PUBLIC_KEY</code> / <code>VAPID_PRIVATE_KEY</code> /
            <code> VAPID_SUBJECT</code>), so nothing could be delivered. Alerts
            on push-only rules stay pending until it is set up, rather than
            being dropped.
          </Alert>
        )}

        {state === "denied" && (
          <Alert severity="warning" variant="outlined">
            Notifications are blocked for this site. Browsers remember that
            choice and will not ask again — re-allow notifications in the site
            settings (the padlock next to the address bar), then reload.
          </Alert>
        )}

        {(state === "off" || state === "on") && (
          <Typography variant="body2" color="text.secondary">
            {state === "on"
              ? "This browser will show a notification when a rule with the Push channel fires."
              : "Get a desktop notification when a rule with the Push channel fires, without watching your inbox."}{" "}
            Alerts reach you when <strong>{email}</strong> is on a rule&rsquo;s
            recipient list, or on the segment list it inherits from General
            settings. Notifications carry the site, the room or device and the
            condition — never resident information.
          </Typography>
        )}

        {otherDevices > 0 && (
          <Typography variant="caption" color="text.secondary">
            {otherDevices} other browser{otherDevices === 1 ? "" : "s"} on this
            account {otherDevices === 1 ? "is" : "are"} also receiving alerts.
          </Typography>
        )}

        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}

        {(state === "off" || state === "on") && (
          <Box>
            {state === "off" ? (
              <Button
                variant="contained"
                size="small"
                disabled={busy}
                onClick={() => void enable()}
                data-testid="staff-push-enable"
              >
                Enable push on this browser
              </Button>
            ) : (
              <Button
                size="small"
                color="inherit"
                disabled={busy}
                onClick={() => void disable()}
                data-testid="staff-push-disable"
              >
                Turn off on this browser
              </Button>
            )}
          </Box>
        )}
      </Stack>
    </Card>
  );
}
